import { useAtom } from "jotai";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import {
    IPricingTemplate,
    IPopulatedPricingTemplate,
    IBrandPricing,
    pricingTemplatesAtom,
    populatedPricingTemplatesAtom,
    internalPricingAtom,
    pricingTemplatesLoadingAtom,
    IPricingSource,
    pricingTemplateSourcesAtom,
    allPricingTemplateSourcesAtom,
    ICostBreakdownResponse,
    costBreakdownAtom,
    IPricingUpdateJob,
    IReferenceRatesResponse,
    referenceRatesAtom,
    IAccountTypeDefaultsResponse,
    IAccountTypePricingDefault,
    ILicensingFeeConfig,
    ILicensingFeeDefault,
    IPricingSyncExclusion,
    IPricingSyncResult,
    LicensingFeeType
} from "./types";

type GetPricingTemplates = (brand_id: number) => Promise<IPricingTemplate[]>;
type GetPopulatedPricingTemplates = (brand_id: number) => Promise<IPopulatedPricingTemplate[]>;
type InsertPricingTemplate = (body: IPopulatedPricingTemplate) => Promise<IPopulatedPricingTemplate | null>;
type GetInternalBrandPricing = (brand_id: number) => Promise<IBrandPricing[] | null>;
type UpdateBrandPricingResult = { templates: IPopulatedPricingTemplate[] | null; jobId: number | null };
type UpdateInternalBrandPricing = (body: IPopulatedPricingTemplate, isInternal: boolean) => Promise<UpdateBrandPricingResult>;
type DeletePricingTemplate = (template_id: number) => Promise<boolean>;
type GetTemplateSources = (all_sources: boolean) => Promise<IPricingSource[] | null>;
type GetCostBreakdown = (brand_id: number) => Promise<ICostBreakdownResponse | null>;
type GetUpdateJob = (id: number) => Promise<IPricingUpdateJob | null>;
type ListUpdateJobs = (brand_id: number) => Promise<IPricingUpdateJob[] | null>;
type GetReferenceRates = () => Promise<IReferenceRatesResponse | null>;
type UpdateReferenceRates = (pricings: { source: string; price: number }[]) => Promise<IReferenceRatesResponse | null>;
type GetAccountTypeDefaults = () => Promise<IAccountTypeDefaultsResponse | null>;
type UpdateAccountTypeDefaults = (defaults: IAccountTypePricingDefault[]) => Promise<IAccountTypeDefaultsResponse | null>;
type GetLicensingFeeConfig = (brand_id: number) => Promise<ILicensingFeeConfig | null>;
type UpdateLicensingFeeConfig = (payload: { brand_id: number; fee_type: LicensingFeeType; fee_value: number }) => Promise<ILicensingFeeConfig | null>;
type ListPricingSyncExclusions = () => Promise<IPricingSyncExclusion[]>;
type ListProtectedBrands = () => Promise<IPricingSyncExclusion[]>;
type AddPricingSyncExclusion = (payload: { brand_id: number; note?: string | null }) => Promise<boolean>;
type RemovePricingSyncExclusion = (brand_id: number) => Promise<boolean>;
type PreviewPricingSync = (payload: { account_type_id: number; exclude_brand_ids?: number[] }) => Promise<IPricingSyncResult | null>;
type ApplyPricingSync = (payload: { account_type_id: number; exclude_brand_ids?: number[]; expected_rows: number }) => Promise<IPricingSyncResult | null>;
type GetLicensingFeeDefault = () => Promise<ILicensingFeeDefault | null>;
type UpdateLicensingFeeDefault = (payload: { fee_type: LicensingFeeType; fee_value: number }) => Promise<ILicensingFeeDefault | null>;

type PricingKeyFunctions = {
    get: GetPricingTemplates,
    getPopulated: GetPopulatedPricingTemplates,
    insert: InsertPricingTemplate,
    getInternal: GetInternalBrandPricing,
    updateInternal: UpdateInternalBrandPricing,
    delete: DeletePricingTemplate,
    getTemplateSources: GetTemplateSources,
    getCostBreakdown: GetCostBreakdown,
    getUpdateJob: GetUpdateJob,
    listUpdateJobs: ListUpdateJobs,
    getReferenceRates: GetReferenceRates,
    updateReferenceRates: UpdateReferenceRates,
    getAccountTypeDefaults: GetAccountTypeDefaults,
    updateAccountTypeDefaults: UpdateAccountTypeDefaults,
    getLicensingFeeConfig: GetLicensingFeeConfig,
    updateLicensingFeeConfig: UpdateLicensingFeeConfig,
    getLicensingFeeDefault: GetLicensingFeeDefault,
    updateLicensingFeeDefault: UpdateLicensingFeeDefault,
    listPricingSyncExclusions: ListPricingSyncExclusions,
    listProtectedBrands: ListProtectedBrands,
    addPricingSyncExclusion: AddPricingSyncExclusion,
    removePricingSyncExclusion: RemovePricingSyncExclusion,
    previewPricingSync: PreviewPricingSync,
    applyPricingSync: ApplyPricingSync
}

type UsePricing = [
    IPricingTemplate[],
    IPopulatedPricingTemplate[],
    IBrandPricing[] | null,
    PricingKeyFunctions
];

export const usePricing = (): UsePricing => {
    const [templates, setTemplates] = useAtom(pricingTemplatesAtom);
    const [populatedTemplates, setPopulatedTemplates] = useAtom(populatedPricingTemplatesAtom);
    const [internalPricing, setInternalPricing] = useAtom(internalPricingAtom);
    const [, setTemplatesLoading] = useAtom(pricingTemplatesLoadingAtom);
    const [, setTemplateSources] = useAtom(pricingTemplateSourcesAtom);
    const [, setAllTemplateSources] = useAtom(allPricingTemplateSourcesAtom);
    const [, setCostBreakdown] = useAtom(costBreakdownAtom);
    const [, setReferenceRates] = useAtom(referenceRatesAtom);

    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const getCostBreakdown = async (brand_id: number): Promise<ICostBreakdownResponse | null> => {
        const response: AxiosResponse<ICostBreakdownResponse> | void = await axios.get(`${environment.pricingUrl}/cost-breakdown?brand_id=${brand_id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (response) {
            setCostBreakdown(response.data);
            return response.data;
        } else {
            setCostBreakdown(null);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error retrieving the Wallmates cost breakdown. Please try again later.'
            });
            return null;
        }
    }

    const getTemplateSources = async (all_sources = false): Promise<IPricingSource[] | null> => {
        const getSources: AxiosResponse<IPricingSource[]> | void = await axios.get(`${environment.pricingUrl}/template-pricing-sources?all_sources=${all_sources}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        });

        if (getSources) {
            if (!all_sources) {
                setTemplateSources(getSources.data);
            } else {
                setAllTemplateSources(getSources.data);
            }
            return getSources.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error retrieving pricing template sources. Please try again later.'
            });
            return [];
        }

    }

    const getPricingTemplates = async (brand_id: number): Promise<IPricingTemplate[]> => {
        setTemplatesLoading(true);
        const getTemplates: AxiosResponse<IPricingTemplate[]> | void = await axios.get(`${environment.pricingUrl}/pricing-templates?brand_id=${brand_id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (getTemplates) {
            setTemplates(getTemplates.data);
            setTemplatesLoading(false);
            return getTemplates.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error retrieving pricing templates. Please try again later.'
            });
            setTemplatesLoading(false);
            return [];
        }
    }

    const getPopulatedPricingTemplates = async (brand_id: number): Promise<IPopulatedPricingTemplate[]> => {
        setTemplatesLoading(true);
        const getTemplates: AxiosResponse<IPopulatedPricingTemplate[]> | void = await axios.get(`${environment.pricingUrl}/populated-templates?brand_id=${brand_id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (getTemplates) {
            setPopulatedTemplates(getTemplates.data);
            setTemplatesLoading(false);
            return getTemplates.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error retrieving pricing templates. Please try again later.'
            });
            setTemplatesLoading(false);
            return [];
        }
    }

    const insertPricingTemplate = async (body: IPopulatedPricingTemplate): Promise<IPopulatedPricingTemplate | null> => {
        const insertResponse: AxiosResponse<IPopulatedPricingTemplate> = await axios.post(environment.pricingUrl, body, {
            headers: {
                "Content-Type": "application/json",
                'X-Wallmates-Auth': authToken
            }
        });

        if (insertResponse) {
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Pricing Template Successfully Created.'
            });
            getPopulatedPricingTemplates(body.brand_id);
            return insertResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error creating pricing template.'
            });
            return null;
        }
    }

    const getBrandPricing = async (brand_id: number): Promise<IBrandPricing[] | null> => {
        const getResponse: AxiosResponse<IBrandPricing[]> | void = await axios.get(`${environment.pricingUrl}/brand-pricing?brand_id=${brand_id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (getResponse) {
            setInternalPricing(getResponse.data);
            return getResponse.data;
        } else {
            setInternalPricing(null);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'Could not get brand pricing.'
            });
            return null;
        }
    }

    const updateBrandPricing = async (body: IPopulatedPricingTemplate, isInternal = false): Promise<UpdateBrandPricingResult> => {
        try {
            const updateResponse: AxiosResponse<IPopulatedPricingTemplate | IPopulatedPricingTemplate[]> = await axios.put(`${environment.pricingUrl}/brand-pricing?is_internal=${isInternal}`, body, {
                headers: {
                    "Content-Type": "application/json",
                    'X-Wallmates-Auth': authToken
                },
                // The PUT itself now returns quickly (it enqueues a background job
                // for "update all products") — but keep a generous ceiling so a
                // slow save never hangs the promise indefinitely.
                timeout: 60_000
            });

            const data: any = updateResponse.data;
            // Backend returns the saved template object; a background job id is
            // present only when "update all products" was requested.
            const jobId: number | null = (data && !Array.isArray(data) && typeof data.pricing_update_job_id === 'number')
                ? data.pricing_update_job_id
                : null;

            // The backend sets this when the template saved but the bulk update
            // could not be queued — surface it loudly instead of showing a
            // success toast for work that will never happen.
            const jobError: string | null = (data && !Array.isArray(data) && typeof data.pricing_update_job_error === 'string')
                ? data.pricing_update_job_error
                : null;

            setSnackbar({
                show: true,
                snackbarLevel: jobError ? 'error' : 'info',
                text: jobError
                    ? jobError
                    : (jobId
                        ? 'Pricing saved. Updating products in the background…'
                        : 'Brand Pricing Successfully Updated.')
            });

            if (!isInternal) {
                getPopulatedPricingTemplates(body.brand_id);
            } else {
                getBrandPricing(body.brand_id);
            }

            return { templates: Array.isArray(data) ? data : [data], jobId };
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error updating brand pricing.'
            });
            return { templates: null, jobId: null };
        }
    }

    const getUpdateJob = async (id: number): Promise<IPricingUpdateJob | null> => {
        try {
            const response: AxiosResponse<IPricingUpdateJob> = await axios.get(`${environment.pricingUrl}/brand-pricing/jobs/${id}`, {
                headers: {
                    'X-Wallmates-Auth': authToken
                },
                timeout: 15_000
            });
            return response?.data ?? null;
        } catch {
            // Polling fallback — a transient failure shouldn't kill the progress UI.
            return null;
        }
    }

    // #region listUpdateJobs
    // Recent bulk-update runs for the brand, newest first. Powers the history
    // view so past failures remain reviewable.
    const listUpdateJobs = async (brand_id: number): Promise<IPricingUpdateJob[] | null> => {
        try {
            const response: AxiosResponse<IPricingUpdateJob[]> = await axios.get(`${environment.pricingUrl}/brand-pricing/jobs?brand_id=${brand_id}`, {
                headers: {
                    'X-Wallmates-Auth': authToken
                },
                timeout: 15_000
            });
            return response?.data ?? null;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            return null;
        }
    }

    const deletePricingTemplate = async (template_id: number): Promise<boolean> => {
        const deleteResponse: AxiosResponse<boolean> | void = await axios.delete(`${environment.pricingUrl}?template_id=${template_id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (deleteResponse) {
            const resetTemplatesResults = populatedTemplates.filter((item: IPopulatedPricingTemplate) => item.id !== template_id);
            setPopulatedTemplates(resetTemplatesResults);
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Pricing Template Successfully Deleted.'
            });
            return deleteResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while deleting pricing template. Please try again later.'
            })
            return false;
        }
    }

    // #region getReferenceRates
    // SUPERADMIN only (403 otherwise). Backs the Fulfillment Fee reference rates
    // admin screen — these rates size the fee, they are not brand pricing.
    const getReferenceRates = async (): Promise<IReferenceRatesResponse | null> => {
        const response: AxiosResponse<IReferenceRatesResponse> | void = await axios.get(`${environment.pricingUrl}/reference-rates`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (response) {
            setReferenceRates(response.data);
            return response.data;
        }

        setReferenceRates(null);
        setSnackbar({
            show: true,
            snackbarLevel: 'error',
            text: 'There was an error retrieving the Fulfillment Fee reference rates. Please try again later.'
        });
        return null;
    }

    // #region updateReferenceRates
    // All rates must be > 0 — the backend rejects the whole payload otherwise,
    // because a zero rate would silently zero that material's contribution to the
    // fee base.
    const updateReferenceRates = async (pricings: { source: string; price: number }[]): Promise<IReferenceRatesResponse | null> => {
        try {
            const response: AxiosResponse<IReferenceRatesResponse> = await axios.put(`${environment.pricingUrl}/reference-rates`, { pricings }, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-Wallmates-Auth': authToken
                },
                timeout: 30_000
            });

            setReferenceRates(response.data);
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Fulfillment Fee reference rates updated.'
            });
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error updating the Fulfillment Fee reference rates.'
            });
            return null;
        }
    }

    // #region getAccountTypeDefaults
    // SUPERADMIN/SUPERUSER only (403 otherwise). Returns the tier grid plus, per
    // account type, how many brands currently sit on it.
    const getAccountTypeDefaults = async (): Promise<IAccountTypeDefaultsResponse | null> => {
        try {
            const response: AxiosResponse<IAccountTypeDefaultsResponse> = await axios.get(`${environment.pricingUrl}/account-type-defaults`, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error loading account type pricing defaults.'
            });
            return null;
        }
    }

    // #region updateAccountTypeDefaults
    // FORWARD-LOOKING ONLY. This changes the price stamped onto newly created
    // brands and brands promoted to STORE_OWNER. It does not touch any existing
    // brand's pricing, and there is no re-stamp action.
    const updateAccountTypeDefaults = async (defaults: IAccountTypePricingDefault[]): Promise<IAccountTypeDefaultsResponse | null> => {
        try {
            const response: AxiosResponse<IAccountTypeDefaultsResponse> = await axios.put(`${environment.pricingUrl}/account-type-defaults`, { defaults }, {
                headers: { 'Content-Type': 'application/json', 'X-Wallmates-Auth': authToken },
                timeout: 30_000
            });

            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Account type pricing defaults updated.'
            });
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error updating account type pricing defaults.'
            });
            return null;
        }
    }

    // #region getLicensingFeeConfig
    const getLicensingFeeConfig = async (brand_id: number): Promise<ILicensingFeeConfig | null> => {
        try {
            const response: AxiosResponse<ILicensingFeeConfig> = await axios.get(`${environment.pricingUrl}/licensing-fee-config?brand_id=${brand_id}`, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error loading the licensing fee configuration.'
            });
            return null;
        }
    }

    // #region updateLicensingFeeConfig
    const updateLicensingFeeConfig = async (payload: { brand_id: number; fee_type: LicensingFeeType; fee_value: number }): Promise<ILicensingFeeConfig | null> => {
        try {
            const response: AxiosResponse<ILicensingFeeConfig> = await axios.put(`${environment.pricingUrl}/licensing-fee-config`, payload, {
                headers: { 'Content-Type': 'application/json', 'X-Wallmates-Auth': authToken }
            });

            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Licensing fee updated.'
            });
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error updating the licensing fee.'
            });
            return null;
        }
    }

    // #region listPricingSyncExclusions
    const listPricingSyncExclusions = async (): Promise<IPricingSyncExclusion[]> => {
        try {
            const response: AxiosResponse<IPricingSyncExclusion[]> = await axios.get(`${environment.pricingUrl}/account-type-sync/exclusions`, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error loading the pricing sync exclusions.'
            });
            return [];
        }
    }

    // #region listProtectedBrands
    // Brands exempted by their own `pricing_protected` flag. Fetched separately from
    // the manual exclusions so the sync screen can render them read-only.
    const listProtectedBrands = async (): Promise<IPricingSyncExclusion[]> => {
        try {
            const response: AxiosResponse<IPricingSyncExclusion[]> = await axios.get(`${environment.pricingUrl}/account-type-sync/protected-brands`, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            return (response.data ?? []).map((entry) => ({ ...entry, is_protected: true }));
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error loading the pricing-protected brands.'
            });
            return [];
        }
    }

    // #region addPricingSyncExclusion
    const addPricingSyncExclusion = async (payload: { brand_id: number; note?: string | null }): Promise<boolean> => {
        try {
            await axios.post(`${environment.pricingUrl}/account-type-sync/exclusions`, payload, {
                headers: { 'Content-Type': 'application/json', 'X-Wallmates-Auth': authToken }
            });
            return true;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error excluding that brand.'
            });
            return false;
        }
    }

    // #region removePricingSyncExclusion
    const removePricingSyncExclusion = async (brand_id: number): Promise<boolean> => {
        try {
            await axios.delete(`${environment.pricingUrl}/account-type-sync/exclusions?brand_id=${brand_id}`, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            return true;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error removing that exclusion.'
            });
            return false;
        }
    }

    // #region previewPricingSync
    // Dry run — writes nothing. Same backend function as apply, so the diff shown
    // is exactly what apply would write.
    const previewPricingSync = async (payload: { account_type_id: number; exclude_brand_ids?: number[] }): Promise<IPricingSyncResult | null> => {
        try {
            const response: AxiosResponse<IPricingSyncResult> = await axios.post(`${environment.pricingUrl}/account-type-sync/preview`, payload, {
                headers: { 'Content-Type': 'application/json', 'X-Wallmates-Auth': authToken }
            });
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error previewing the pricing sync.'
            });
            return null;
        }
    }

    // #region applyPricingSync
    // `expected_rows` is the count from the preview. The backend re-checks it and
    // rejects with 409 if the data moved, so a stale preview cannot write.
    const applyPricingSync = async (payload: {
        account_type_id: number;
        exclude_brand_ids?: number[];
        expected_rows: number;
    }): Promise<IPricingSyncResult | null> => {
        try {
            const response: AxiosResponse<IPricingSyncResult> = await axios.post(`${environment.pricingUrl}/account-type-sync/apply`, payload, {
                headers: { 'Content-Type': 'application/json', 'X-Wallmates-Auth': authToken }
            });

            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: `Pricing synced to ${response.data.brands_affected} brand${response.data.brands_affected === 1 ? '' : 's'} (${response.data.rows_changed} rate${response.data.rows_changed === 1 ? '' : 's'}).`
            });
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error applying the pricing sync.'
            });
            return null;
        }
    }

    // #region getLicensingFeeDefault
    const getLicensingFeeDefault = async (): Promise<ILicensingFeeDefault | null> => {
        try {
            const response: AxiosResponse<ILicensingFeeDefault> = await axios.get(`${environment.pricingUrl}/licensing-fee-default`, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error loading the global licensing fee default.'
            });
            return null;
        }
    }

    // #region updateLicensingFeeDefault
    const updateLicensingFeeDefault = async (payload: { fee_type: LicensingFeeType; fee_value: number }): Promise<ILicensingFeeDefault | null> => {
        try {
            const response: AxiosResponse<ILicensingFeeDefault> = await axios.put(`${environment.pricingUrl}/licensing-fee-default`, payload, {
                headers: { 'Content-Type': 'application/json', 'X-Wallmates-Auth': authToken }
            });

            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Global licensing fee default updated.'
            });
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error updating the global licensing fee default.'
            });
            return null;
        }
    }

    const keyFunctions: PricingKeyFunctions = {
        get: getPricingTemplates,
        getPopulated: getPopulatedPricingTemplates,
        insert: insertPricingTemplate,
        getInternal: getBrandPricing,
        updateInternal: updateBrandPricing,
        delete: deletePricingTemplate,
        getTemplateSources: getTemplateSources,
        getCostBreakdown: getCostBreakdown,
        getUpdateJob: getUpdateJob,
        listUpdateJobs: listUpdateJobs,
        getReferenceRates: getReferenceRates,
        updateReferenceRates: updateReferenceRates,
        getAccountTypeDefaults: getAccountTypeDefaults,
        updateAccountTypeDefaults: updateAccountTypeDefaults,
        getLicensingFeeConfig: getLicensingFeeConfig,
        updateLicensingFeeConfig: updateLicensingFeeConfig,
        getLicensingFeeDefault: getLicensingFeeDefault,
        updateLicensingFeeDefault: updateLicensingFeeDefault,
        listPricingSyncExclusions: listPricingSyncExclusions,
        listProtectedBrands: listProtectedBrands,
        addPricingSyncExclusion: addPricingSyncExclusion,
        removePricingSyncExclusion: removePricingSyncExclusion,
        previewPricingSync: previewPricingSync,
        applyPricingSync: applyPricingSync
    }

    return [
        templates,
        populatedTemplates,
        internalPricing,
        keyFunctions
    ]
}